import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { revokeCurrentServerSession } from '@/utils/session-actions'
import { useAuth } from '@clerk/clerk-react'
import { LoaderCircle, LogOut, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

interface LogoutModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  title?: string
  description?: string
}

const LogoutModal: React.FC<LogoutModalProps> = ({
  open,
  setOpen,
  title = 'Sign Out',
  description = 'Are you sure you want to sign out of your account?',
}) => {
  const [confirm, setConfirm] = useState(false)
  const { signOut } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (confirm) {
      void (async () => {
        try {
          try {
            await revokeCurrentServerSession()
          } catch {
            // Continue clearing client session even if server revocation fails.
          }

          await signOut()
          navigate('/sign-in', {
            replace: true,
          })
          toast.success('Signed out successfully!', {
            description: 'You have been signed out of your account',
          })
        } catch (err: unknown) {
          console.error(err)
        } finally {
          setConfirm(false)
          setOpen(false)
        }
      })()
    }
  }, [confirm])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className='max-w-[420px] z-[9999] p-0 gap-0 overflow-hidden border-0 shadow-xl'
        overlayClassName='z-[9999] bg-black/60 backdrop-blur-sm'
        showCloseButton={false}
      >
        <DialogHeader className='flex flex-row items-center justify-between p-6 border-b dark:border-gray-700'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center'>
              <LogOut className='w-5 h-5 text-red-600 dark:text-red-400' />
            </div>
            <DialogTitle className='text-lg font-semibold text-gray-900 dark:text-white m-0'>{title}</DialogTitle>
          </div>
          <button
            type='button'
            className='w-8 h-8 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors duration-200'
            onClick={() => setOpen(false)}
          >
            <X className='w-4 h-4 text-gray-500 dark:text-gray-400' />
          </button>
        </DialogHeader>

        <div className='p-6'>
          <DialogDescription className='text-gray-700 dark:text-gray-300 mb-4 text-left'>
            {description}
          </DialogDescription>

          <div className='mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg'>
            <p className='text-sm text-red-800 dark:text-red-200 font-medium flex items-center gap-2'>
              <span className='w-1.5 h-1.5 bg-red-500 rounded-full'></span>
              You will need to sign in again.
            </p>
          </div>
        </div>

        <DialogFooter className='flex flex-col-reverse sm:flex-row gap-3 justify-end px-6 pb-6 pt-0 border-0'>
          <Button variant='outline' onClick={() => setOpen(false)} disabled={confirm} className='w-full sm:w-auto'>
            Cancel
          </Button>
          <Button
            disabled={confirm}
            onClick={() => setConfirm(true)}
            variant='destructive'
            className='w-full sm:w-auto'
          >
            {confirm ? (
              <>
                <LoaderCircle className='w-4 h-4 animate-spin' />
                Signing out...
              </>
            ) : (
              <>
                <LogOut className='w-4 h-4' />
                Sign Out
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LogoutModal
